import React, { useEffect, useState } from "react";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://rfs.react.eblackdog.net/api";

const IconUser = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const IconLock = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const IconEyeOpen = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconEyeClosed = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const IconPaw = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <ellipse cx="6" cy="6" rx="2.5" ry="3" />
    <ellipse cx="12" cy="4" rx="2.5" ry="3" />
    <ellipse cx="18" cy="6" rx="2.5" ry="3" />
    <ellipse cx="3.5" cy="12" rx="2" ry="2.5" />
    <path d="M12 22c-4 0-8-3-8-7 0-2 1.5-3.5 3-4.5 1-.7 2.5-1.5 5-1.5s4 .8 5 1.5c1.5 1 3 2.5 3 4.5 0 4-4 7-8 7z" />
  </svg>
);

const IconCheck = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export default function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    remember: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState({
    visible: false,
    message: "",
    type: "info",
  });
  const [loading, setLoading] = useState(false);

  const showStatus = (message, type = "info") => {
    setStatus({
      visible: true,
      message,
      type,
    });
  };

  useEffect(() => {
    const remembered = localStorage.getItem("rememberedUsername");

    if (remembered) {
      setFormData((prev) => ({
        ...prev,
        username: remembered,
        remember: true,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const clearOldGuardLoginData = () => {
    [
      "guardToken",
      "guardData",
      "guardLoginType",
      "isGuardLoggedIn",
      "memberId",
      "guardName",
      "guardEmail",
      "designation",
    ].forEach((key) => localStorage.removeItem(key));
  };

  const startGpsOnLogin = async (token = "") => {
    if (!navigator.geolocation) return;

    const position = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    });

    if (!position || !position.coords) return;

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const accuracy = position.coords.accuracy || 0;

    if (
      Number.isNaN(latitude) ||
      Number.isNaN(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/gps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "ensure",
          latitude,
          longitude,
          accuracy,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data?.success && data?.tracking_id) {
        sessionStorage.setItem("gps_tracking_id", String(data.tracking_id));
      }
    } catch (error) {
      console.error("[AdminLogin] GPS start error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const username = formData.username.trim();
    const password = formData.password;

    if (!username || !password.trim()) {
      showStatus("Please enter both username and password", "error");
      return;
    }

    setLoading(true);
    showStatus("Authenticating your credentials...", "info");

    try {
      const res = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        showStatus(result.message || "Invalid username or password", "error");
        setLoading(false);
        return;
      }

      const token = result.token;
      const rawUser = result.user || result.data || {};

      if (!token) {
        showStatus("Login success but token missing from backend", "error");
        setLoading(false);
        return;
      }

      const adminId =
        rawUser.adminId ||
        rawUser.admin_id ||
        rawUser.id ||
        rawUser.SINo ||
        rawUser.sinno ||
        null;

      if (!adminId) {
        showStatus("Login success but admin ID missing", "error");
        setLoading(false);
        return;
      }

      const userInfo = {
        ...rawUser,
        id: adminId,
        adminId,
        role: rawUser.role || "admin",
        loginType: "admin",
      };

      if (formData.remember) {
        localStorage.setItem("rememberedUsername", username);
      } else {
        localStorage.removeItem("rememberedUsername");
      }

      clearOldGuardLoginData();

      localStorage.setItem("token", token);
      localStorage.setItem("authToken", token);
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginType", "admin");
      localStorage.setItem("role", userInfo.role);
      localStorage.setItem("userData", JSON.stringify(userInfo));
      localStorage.setItem("adminId", String(adminId));

      localStorage.setItem("company_name", userInfo.company_name || "");
      localStorage.setItem("contact_name", userInfo.contact_name || "");
      localStorage.setItem("company_phone", userInfo.phone || "");
      localStorage.setItem("admin_email", userInfo.email || "");
      localStorage.setItem("company_address", userInfo.address || "");
      localStorage.setItem("company_logo", userInfo.logo_path || "");
      localStorage.setItem("user_map", userInfo.user_map || "");

      showStatus("Login successful! Getting your location...", "success");

      try {
        await Promise.race([
          startGpsOnLogin(token),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (error) {
        console.error("[AdminLogin] GPS race error:", error);
      }

      window.location.replace("/dashboard");
    } catch (error) {
      console.error("[AdminLogin] Login error:", error);
      showStatus("Network error. Please try again.", "error");
      setLoading(false);
    }
  };

  const statusConfig = {
    success: { cls: "status-success", icon: <IconCheck /> },
    error: { cls: "status-error", icon: <IconAlert /> },
    info: { cls: "status-info", icon: <IconInfo /> },
  };

  const { cls: statusCls, icon: statusIcon } =
    statusConfig[status.type] || statusConfig.info;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .l-root {
          min-height: 100vh;
          background-color: #111111;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          font-family: 'Inter', system-ui, sans-serif;
          position: relative;
          overflow: hidden;
        }

        .l-root::before {
          content: '';
          position: absolute;
          top: -160px;
          left: -160px;
          width: 520px;
          height: 520px;
          border-radius: 50%;
          border: 1px solid rgba(58, 155, 82, 0.14);
          pointer-events: none;
        }

        .l-root::after {
          content: '';
          position: absolute;
          bottom: -100px;
          right: -100px;
          width: 360px;
          height: 360px;
          border-radius: 50%;
          border: 1px solid rgba(58, 155, 82, 0.09);
          pointer-events: none;
        }

        .l-card {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 430px;
          background: #1A1A1A;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid #2A2A2A;
          box-shadow: 0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(58,155,82,0.08);
        }

        .l-header {
          background-color: #1E1E1E;
          border-bottom: 3px solid #3A9B52;
          padding: 36px 40px 32px;
          text-align: center;
        }

        .l-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(58, 155, 82, 0.12);
          border: 1px solid rgba(58, 155, 82, 0.35);
          color: #3A9B52;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 5px 13px;
          border-radius: 20px;
          margin-bottom: 22px;
        }

        .l-logo-ring {
          margin: 0 auto 18px;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          padding: 5px;
          background: #111111;
          border: 2.5px solid #3A9B52;
          box-shadow: 0 0 0 4px rgba(58,155,82,0.15), 0 12px 28px rgba(0,0,0,0.5);
          overflow: hidden;
        }

        .l-logo-ring img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          display: block;
        }

        .l-header h1 {
          color: #FFFFFF;
          font-size: 24px;
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .l-header p {
          color: #6B7280;
          font-size: 13px;
          margin-top: 5px;
          font-weight: 400;
        }

        .l-body {
          padding: 32px 40px 28px;
        }

        .l-group {
          margin-bottom: 18px;
        }

        .l-label {
          display: block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #9CA3AF;
          margin-bottom: 7px;
        }

        .l-input-wrap {
          position: relative;
        }

        .l-input-icon {
          position: absolute;
          left: 13px;
          top: 50%;
          transform: translateY(-50%);
          color: #3A9B52;
          display: flex;
          align-items: center;
          pointer-events: none;
        }

        .l-input {
          width: 100%;
          background: #111111;
          border: 1.5px solid #2E2E2E;
          border-radius: 11px;
          padding: 12.5px 13px 12.5px 42px;
          font-size: 14px;
          font-weight: 500;
          color: #F3F4F6;
          font-family: inherit;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s;
          caret-color: #3A9B52;
        }

        .l-input::placeholder {
          color: #4B5563;
          font-weight: 400;
        }

        .l-input:focus {
          border-color: #3A9B52;
          box-shadow: 0 0 0 3px rgba(58, 155, 82, 0.15);
        }

        .l-eye {
          position: absolute;
          right: 13px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #4B5563;
          display: flex;
          align-items: center;
          padding: 4px;
          border-radius: 6px;
          transition: color 0.15s;
        }

        .l-eye:hover {
          color: #3A9B52;
        }

        .l-options {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 22px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .l-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #9CA3AF;
          cursor: pointer;
          font-weight: 500;
          user-select: none;
        }

        .l-checkbox input[type="checkbox"] {
          width: 15px;
          height: 15px;
          accent-color: #3A9B52;
          cursor: pointer;
        }

        .l-forgot {
          font-size: 13px;
          font-weight: 600;
          color: #3A9B52;
          text-decoration: none;
          transition: color 0.15s;
        }

        .l-forgot:hover {
          color: #4DB866;
        }

        .l-btn {
          width: 100%;
          background-color: #3A9B52;
          color: #FFFFFF;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 14px 20px;
          border: none;
          border-radius: 11px;
          cursor: pointer;
          font-family: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: background-color 0.18s, transform 0.1s, box-shadow 0.18s;
          box-shadow: 0 4px 18px rgba(58, 155, 82, 0.3);
        }

        .l-btn:hover:not(:disabled) {
          background-color: #2E8042;
          box-shadow: 0 6px 24px rgba(58, 155, 82, 0.45);
        }

        .l-btn:active:not(:disabled) {
          transform: scale(0.985);
        }

        .l-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .l-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: lspin 0.65s linear infinite;
          flex-shrink: 0;
        }

        @keyframes lspin {
          to {
            transform: rotate(360deg);
          }
        }

        .l-status {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 14px;
          padding: 11px 14px;
          border-radius: 10px;
          border: 1px solid;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.4;
        }

        .l-status-icon {
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .status-success {
          background: rgba(58,155,82,0.08);
          border-color: rgba(58,155,82,0.35);
          color: #4DB866;
        }

        .status-error {
          background: rgba(239,68,68,0.08);
          border-color: rgba(239,68,68,0.3);
          color: #F87171;
        }

        .status-info {
          background: rgba(59,130,246,0.08);
          border-color: rgba(59,130,246,0.3);
          color: #60A5FA;
        }

        .l-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0 0;
        }

        .l-divider-line {
          flex: 1;
          height: 1px;
          background: #2A2A2A;
        }

        .l-divider-text {
          font-size: 11px;
          color: #4B5563;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .l-footer {
          padding: 20px 40px 26px;
          text-align: center;
        }

        .l-footer-links {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          margin-bottom: 12px;
        }

        .l-footer-links a,
        .l-footer-links span {
          font-size: 12px;
          color: #4B5563;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.15s;
        }

        .l-footer-links a:hover {
          color: #3A9B52;
        }

        .l-footer-links .l-phone {
          color: #6B7280;
          font-weight: 600;
        }

        .l-brand {
          font-size: 11px;
          color: #374151;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .l-brand-dot {
          width: 5px;
          height: 5px;
          background: #3A9B52;
          border-radius: 50%;
        }

        @media (max-width: 480px) {
          .l-header {
            padding: 28px 24px 24px;
          }

          .l-body {
            padding: 24px 24px 20px;
          }

          .l-footer {
            padding: 16px 24px 22px;
          }
        }
      `}</style>

      <div className="l-root">
        <div className="l-card">
          <div className="l-header">
            <div className="l-eyebrow">
              <IconPaw />
              eBLACKDOG Patrol and Sentinel System
            </div>

            <div className="l-logo-ring">
              <img src={logo} alt="eBLACKDOG Logo" />
            </div>

            <h1>Admin Sign In</h1>
            <p>Enter your credentials to access the dashboard</p>
          </div>

          <div className="l-body">
            <form onSubmit={handleSubmit}>
              <div className="l-group">
                <label htmlFor="username" className="l-label">
                  Username
                </label>

                <div className="l-input-wrap">
                  <span className="l-input-icon">
                    <IconUser />
                  </span>

                  <input
                    type="text"
                    id="username"
                    name="username"
                    className="l-input"
                    placeholder="Enter your username"
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="l-group">
                <label htmlFor="password" className="l-label">
                  Password
                </label>

                <div className="l-input-wrap">
                  <span className="l-input-icon">
                    <IconLock />
                  </span>

                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="l-input"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    style={{ paddingRight: "42px" }}
                  />

                  <button
                    type="button"
                    className="l-eye"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                  </button>
                </div>
              </div>

              <div className="l-options">
                <label className="l-checkbox">
                  <input
                    type="checkbox"
                    name="remember"
                    checked={formData.remember}
                    onChange={handleChange}
                  />
                  Remember me
                </label>

                <a href="#" className="l-forgot">
                  Forgot Password?
                </a>
              </div>

              <button type="submit" className="l-btn" disabled={loading}>
                {loading ? <span className="l-spinner" /> : null}
                {loading ? "Authenticating..." : "Sign In"}
              </button>
            </form>

            {status.visible && (
              <div className={`l-status ${statusCls}`}>
                <span className="l-status-icon">{statusIcon}</span>
                <span>{status.message}</span>
              </div>
            )}

            <div className="l-divider">
              <div className="l-divider-line" />
              <span className="l-divider-text">Support</span>
              <div className="l-divider-line" />
            </div>
          </div>

          <div className="l-footer">
            <div className="l-footer-links">
              <a href="mailto:eblackdogsupport@nissiinfotech.in">
                eblackdogsupport@nissiinfotech.in
              </a>

              <a
                href="https://www.eblackdog.com"
                target="_blank"
                rel="noreferrer"
              >
                www.eblackdog.com
              </a>

              <span className="l-phone">90037-44644 / 98422-44488</span>
            </div>

            <div className="l-brand">
              <span className="l-brand-dot" />
              <span>© eBLACKDOG Patrol Management System</span>
              <span className="l-brand-dot" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}